import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getUserEmailHistory} from 'src/openapi/admin/apis/UsersEmailsHistoryApi';
import {UserEmailDeliveryLogResponse} from 'src/openapi/admin/models/UserEmailDeliveryLogResponse';
import {useAppDispatch} from 'src/store/hooks';

import {
    userEmailDeliveryLogStateSelector,
} from '../slices/user-email-delivery-log-state.slice';
import {
    userEmailDeliveryLogActions, userEmailDeliveryLogDataSelector, userEmailDeliveryLogResultSelector,
} from '../slices/user-email-delivery-log.slice';
import {UserEmailLogsCriteria} from '../typedef';

import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';

type GetUserEmailDeliveryLogCacheKeys = CacheKeys<undefined, undefined, UserEmailLogsCriteria>;

type Props = {
    userId: number,
};

export const useGetUserEmailDeliveryLog = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: GetUserEmailDeliveryLogCacheKeys) => apiDispatch(
        getUserEmailHistory({
            userEmailDeliveryLogRequest: {
                userIds: [userId],
                pagination: cacheKeys.pagination,
                email: cacheKeys.filters.email as string | undefined,
                status: cacheKeys.filters.status as string | undefined,
                startDate: cacheKeys.filters.startDate as string | undefined,
                endDate: cacheKeys.filters.endDate as string | undefined,
            },
        }),
    ), [userId, apiDispatch]);

    const setLoadedData = useCallback((
        response: UserEmailDeliveryLogResponse,
        cacheKeys: GetUserEmailDeliveryLogCacheKeys,
    ) => {
        dispatch(userEmailDeliveryLogActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data: response.events,
        }));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: userEmailDeliveryLogDataSelector,
        stateSelector: userEmailDeliveryLogStateSelector,
        resultSelector: userEmailDeliveryLogResultSelector,
        actions: userEmailDeliveryLogActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
